import React from "react";
import { SimpleGrid, Text, Box, Button, Flex } from "@chakra-ui/react";
import { Link } from "ui/link";
import { Checklist, ChecklistItem } from "ui/checklist";
import { Balancer } from "react-wrap-balancer";
import { StarterCardDetails } from "./starter-details";
import { BusinessCardDetails } from "./business-details";

type PricingCard = {
  heading: "Starter" | "Business";
  description: string;
  ctaText: string;
  ctaHref: string;
  ctaSubtext: string;
  detailsHeading: string;
  checklistHeading: string;
  checklist: string[];
};

const cards: PricingCard[] = [
  {
    heading: "Starter",
    description: "Plans for teams looking to quickly get data to their tools with the Reverse ETL Platform.",
    ctaText: "Start for free",
    ctaHref: "https://app.hightouch.io/signup",
    ctaSubtext: "No credit card required. Get set up in 23 minutes.",
    detailsHeading: "Choose your plan after 30 days.",
    checklistHeading: "All plans include:",
    checklist: [
      "5 user seats",
      "Unlimited 30-day free trials of any additional destinations",
      "25 free syncs to lite destinations (Slack, Google Sheets, and more)",
      "Unlimited data fields, sources, and source rows",
      "Standard security",
      "Basic observability",
      "Basic extensions",
    ],
  },
  {
    heading: "Business",
    description:
      "Plans for scaled teams with advanced security requirements, or that need additional products like a visual audience builder.",
    ctaText: "Contact sales",
    ctaHref: "/demo?ref=/pricing",
    ctaSubtext: "Only pay for what you need.",
    detailsHeading: "Product selection:",
    checklistHeading: "Included:",
    checklist: [
      "Unlimited user seats",
      "Premium extensions",
      "Advanced observability",
      "Enterprise security",
      "Advanced access management",
      "Dedicated expert support",
      "Custom SLAs",
    ],
  },
];

const DISCLAIMER_TEXT =
  " *Refers to pricing when Hightouch is billed annually, which includes a 20% discount to monthly billing.";

export function PricingCards() {
  return (
    <>
      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        alignItems="stretch"
        justifyContent="center"
        gap={{ base: 7, lg: 5 }}
        mt={{ base: 12, sm: 16, md: 24 }}
        maxW={{ base: "600px", lg: "100%" }}
        mx="auto"
        w="100%"
      >
        {cards.map((card, index) => (
          <Flex key={card.heading} direction="column" borderRadius="lg" bg="white">
            <Flex
              align="start"
              border="1px"
              borderColor="base.border"
              borderRadius="lg"
              boxShadow="base"
              direction="column"
              fontSize="sm"
              flex={1}
              w="100%"
              minH={0}
              p={{ base: 5, sm: 7, md: 10 }}
            >
              <Text as="h2" fontSize={{ base: "xl", lg: "2xl" }} fontWeight="semibold" lineHeight="1.4">
                {card.heading}
              </Text>

              <Text mt={5} w="100%">
                <Balancer>{card.description}</Balancer>
              </Text>

              <Button
                as={Link}
                href={card.ctaHref}
                variant={card.heading === "Starter" ? "teal" : "grass"}
                size="lg"
                mt={7}
                w={{ base: "100%", sm: "auto" }}
              >
                {card.ctaText}
              </Button>

              <Text mt={2}>{card.ctaSubtext}</Text>

              <Text mt={9} fontSize="lg" fontWeight="semibold">
                {card.detailsHeading}
              </Text>

              <Box mt={4} w="100%" maxW="600px">
                {card.heading === "Starter" ? <StarterCardDetails /> : <BusinessCardDetails />}
              </Box>

              <Box mt="auto" pt={8}>
                <Text fontSize="lg" fontWeight="semibold">
                  {card.checklistHeading}
                </Text>

                <Checklist mt={4}>
                  {card.checklist.map((listItem) => (
                    <ChecklistItem key={listItem}>{listItem}</ChecklistItem>
                  ))}
                </Checklist>
              </Box>
            </Flex>

            {index === 0 && (
              <Text display={{ base: "block", lg: "none" }} fontSize="xs" maxW="600px" mx="auto" mt={4}>
                <Balancer>{DISCLAIMER_TEXT}</Balancer>
              </Text>
            )}
          </Flex>
        ))}
      </SimpleGrid>

      <Text display={{ base: "none", lg: "block" }} fontSize="xs" mt={8} width="100%">
        <Balancer>{DISCLAIMER_TEXT}</Balancer>
      </Text>
    </>
  );
}
